import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import SubmitRequestDialogIcon from "@entrilia/shared/icons/SubmitRequestDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { api } from "../../../api/client";
import type { DataCollectionSubmissionDetails } from "../../../api/types/dataCollectionTypes";

interface Props {
  submission: DataCollectionSubmissionDetails;
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

const submitDataSubmission = withErrorHandling(api.dataCollection.submitDataSubmission);

const SubmitConfirmationDialog = ({ open, onClose, onSubmitted, submission }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const [, error] = await submitDataSubmission(submission.id);
    setIsSubmitting(false);

    if (error) {
      logError(error, "[SubmitConfirmationDialog] submitDataSubmission");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Submission successful");
    onSubmitted();
  };

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Submit</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <SubmitRequestDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography noWrap variant="h6">
              Submit {submission.requestName}?
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to submit this form.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={isSubmitting} onClick={handleSubmit}>
          Submit
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default SubmitConfirmationDialog;
